import { useEffect } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

interface UseInfiniteScrollParams {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

/**
 * Triggers `onLoadMore` whenever the returned sentinel ref scrolls into view.
 * Attach the ref to an empty element at the end of the list:
 *
 *   const sentinelRef = useInfiniteScroll({ onLoadMore, hasMore, isLoading });
 *   ...
 *   <div ref={sentinelRef} />
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
}: UseInfiniteScrollParams): (node: Element | null) => void {
  const { isIntersecting, ref } = useIntersectionObserver({ threshold: 0 });

  useEffect(() => {
    if (isIntersecting && hasMore && !isLoading) onLoadMore();
  }, [isIntersecting, hasMore, isLoading, onLoadMore]);

  return ref;
}
